<script setup lang="ts">
import type { AttentionInterlocutor } from '@/stores/useAttentionPointsStore'
import UserSelect from '@/components/admin/UserSelect.vue'

const props = defineProps<{ modelValue: AttentionInterlocutor[] }>()
const emit = defineEmits<{ (e: 'update:modelValue', value: AttentionInterlocutor[]): void }>()

function update(list: AttentionInterlocutor[]) {
  emit('update:modelValue', list)
}

function addInterne() {
  update([...props.modelValue, { kind: 'INTERNE', userUuid: '' }])
}
function addExterne() {
  update([...props.modelValue, { kind: 'EXTERNE', firstName: '', lastName: '', email: '', company: '' }])
}
function removeAt(index: number) {
  update(props.modelValue.filter((_, i) => i !== index))
}
function patchAt(index: number, patch: Partial<AttentionInterlocutor>) {
  update(props.modelValue.map((it, i) => (i === index ? { ...it, ...patch } : it)))
}
</script>

<template>
  <div class="flex flex-col gap-2">
    <div v-if="modelValue.length === 0" class="text-sm text-base-content/60 italic">
      Aucun interlocuteur — ajoutez-en au moins un.
    </div>

    <div
      v-for="(it, index) in modelValue"
      :key="index"
      class="border border-base-300 rounded p-2 flex flex-col gap-2 bg-base-50"
    >
      <div class="flex items-center gap-2">
        <select
          class="select select-bordered select-xs"
          :value="it.kind"
          @change="patchAt(index, { kind: ($event.target as HTMLSelectElement).value as 'INTERNE' | 'EXTERNE' })"
        >
          <option value="INTERNE">Interne</option>
          <option value="EXTERNE">Externe</option>
        </select>

        <template v-if="it.kind === 'INTERNE'">
          <div class="flex-1">
            <UserSelect
              :model-value="it.userUuid"
              placeholder="Rechercher un collaborateur…"
              @update:model-value="patchAt(index, { userUuid: $event ?? '' })"
            />
          </div>
        </template>

        <button type="button" class="btn btn-xs btn-error btn-outline ml-auto" @click="removeAt(index)">
          Retirer
        </button>
      </div>

      <div v-if="it.kind === 'EXTERNE'" class="grid grid-cols-2 md:grid-cols-4 gap-2">
        <input
          :value="it.firstName"
          type="text"
          placeholder="Prénom"
          class="input input-bordered input-xs"
          @input="patchAt(index, { firstName: ($event.target as HTMLInputElement).value })"
        />
        <input
          :value="it.lastName"
          type="text"
          placeholder="Nom"
          class="input input-bordered input-xs"
          @input="patchAt(index, { lastName: ($event.target as HTMLInputElement).value })"
        />
        <input
          :value="it.email"
          type="email"
          placeholder="Email"
          class="input input-bordered input-xs"
          @input="patchAt(index, { email: ($event.target as HTMLInputElement).value })"
        />
        <input
          :value="it.company"
          type="text"
          placeholder="Entreprise"
          class="input input-bordered input-xs"
          @input="patchAt(index, { company: ($event.target as HTMLInputElement).value })"
        />
      </div>
    </div>

    <div class="flex gap-2">
      <button type="button" class="btn btn-xs btn-outline" @click="addInterne">+ Interne</button>
      <button type="button" class="btn btn-xs btn-outline" @click="addExterne">+ Externe</button>
    </div>
    <p class="text-xs text-base-content/50">Un externe doit avoir au moins : nom, prénom, email ou entreprise.</p>
  </div>
</template>
